// src/useAuth.ts
import { useState, useEffect, useCallback } from "react";
var DEFAULT_AUTH_ENDPOINT = "/api/auth/me";
var DEFAULT_TOKEN_AUTH_ENDPOINT = "https://login.weretrade.com/api/auth/me";
var DEFAULT_LOGIN_URL = "https://login.weretrade.com/login";
var TOKEN_STORAGE_KEY = "wt_auth_token";
function getAuthToken() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get("session");
  if (urlToken) {
    localStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
    const url = new URL(window.location.href);
    url.searchParams.delete("session");
    window.history.replaceState({}, "", url.toString());
    return urlToken;
  }
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}
function clearAuthToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}
function useAuth(options = {}) {
  const {
    requireAdmin = false,
    redirectOnUnauthenticated = true,
    redirectUrl,
    authEndpoint = DEFAULT_AUTH_ENDPOINT,
    loginUrl = DEFAULT_LOGIN_URL
  } = options;
  const [state, setState] = useState({
    user: null,
    profile: null,
    loading: true,
    isAdmin: false,
    isAuthenticated: false,
    error: null
  });
  const checkAuth = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const token = getAuthToken();
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const endpoint = token && authEndpoint === DEFAULT_AUTH_ENDPOINT ? DEFAULT_TOKEN_AUTH_ENDPOINT : authEndpoint;
      const res = await fetch(endpoint, {
        credentials: "include",
        // Still include cookies for .weretrade.com domains
        headers
      });
      if (!res.ok) {
        clearAuthToken();
        if (redirectOnUnauthenticated) {
          const redirect = redirectUrl || window.location.href;
          window.location.href = `${loginUrl}?redirect=${encodeURIComponent(redirect)}`;
          return;
        }
        setState({
          user: null,
          profile: null,
          loading: false,
          isAdmin: false,
          isAuthenticated: false,
          error: null
        });
        return;
      }
      const data = await res.json();
      const isAdmin = data.profile?.role === "admin";
      setState({
        user: data.user,
        profile: data.profile,
        loading: false,
        isAdmin,
        isAuthenticated: true,
        error: requireAdmin && !isAdmin ? "Access denied. Admin privileges required." : null
      });
    } catch (err) {
      clearAuthToken();
      setState({
        user: null,
        profile: null,
        loading: false,
        isAdmin: false,
        isAuthenticated: false,
        error: err instanceof Error ? err.message : "Failed to verify authentication"
      });
    }
  }, [authEndpoint, loginUrl, redirectOnUnauthenticated, redirectUrl, requireAdmin]);
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  return { ...state, refresh: checkAuth };
}
function useDisplayName(fallback = "User") {
  const { profile, loading } = useAuth({ redirectOnUnauthenticated: false });
  if (loading) return "...";
  return profile?.display_name || profile?.email || fallback;
}

// src/useFetch.ts
import { useState as useState2, useEffect as useEffect2, useCallback as useCallback2, useRef } from "react";
function useFetch(url, options = {}) {
  const {
    skip = false,
    transform,
    fallback,
    onSuccess,
    onError,
    credentials = "include",
    ...fetchOptions
  } = options;
  const [data, setData] = useState2(null);
  const [loading, setLoading] = useState2(!skip);
  const [error, setError] = useState2(null);
  const mountedRef = useRef(true);
  const fetchData = useCallback2(async () => {
    if (skip) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(url, {
        credentials,
        ...fetchOptions
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const json = await res.json();
      const result = transform ? transform(json) : json;
      if (mountedRef.current) {
        setData(result);
        setError(null);
        onSuccess?.(result);
      }
    } catch (err) {
      if (mountedRef.current) {
        const errorMessage = err instanceof Error ? err.message : "Fetch failed";
        setError(errorMessage);
        if (fallback !== void 0) {
          setData(fallback);
        }
        onError?.(err instanceof Error ? err : new Error(errorMessage));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [url, skip, credentials, transform, fallback, onSuccess, onError, JSON.stringify(fetchOptions)]);
  useEffect2(() => {
    mountedRef.current = true;
    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);
  return { data, loading, error, refetch: fetchData, setData };
}
function useMutation(url, options = {}) {
  const { method = "POST", transform, onSuccess, onError, credentials = "include", ...fetchOptions } = options;
  const [data, setData] = useState2(null);
  const [loading, setLoading] = useState2(false);
  const [error, setError] = useState2(null);
  const mutate = useCallback2(
    async (body) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(url, {
          method,
          credentials,
          headers: {
            "Content-Type": "application/json",
            ...fetchOptions.headers
          },
          body: body !== void 0 ? JSON.stringify(body) : void 0,
          ...fetchOptions
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        const json = await res.json();
        const result = transform ? transform(json) : json;
        setData(result);
        onSuccess?.(result);
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Mutation failed";
        setError(errorMessage);
        onError?.(err instanceof Error ? err : new Error(errorMessage));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [url, method, credentials, transform, onSuccess, onError, JSON.stringify(fetchOptions)]
  );
  const reset = useCallback2(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);
  return { data, loading, error, mutate, reset };
}

// src/usePolling.ts
import { useEffect as useEffect3, useCallback as useCallback3, useRef as useRef2, useState as useState3 } from "react";
function usePolling(callback, options = {}) {
  const { interval = 6e4, immediate = true, paused = false, pauseOnHidden = true } = options;
  const [isPolling, setIsPolling] = useState3(immediate && !paused);
  const [lastPoll, setLastPoll] = useState3(null);
  const callbackRef = useRef2(callback);
  const intervalRef = useRef2(null);
  useEffect3(() => {
    callbackRef.current = callback;
  }, [callback]);
  const poll = useCallback3(async () => {
    await callbackRef.current();
    setLastPoll(Date.now());
  }, []);
  const start = useCallback3(() => {
    setIsPolling(true);
  }, []);
  const stop = useCallback3(() => {
    setIsPolling(false);
  }, []);
  useEffect3(() => {
    if (!isPolling || paused) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (immediate) {
      poll();
    }
    intervalRef.current = window.setInterval(poll, interval);
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, paused, interval, immediate, poll]);
  useEffect3(() => {
    if (!pauseOnHidden) return;
    const handleVisibility = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else if (isPolling && !paused) {
        poll();
        intervalRef.current = window.setInterval(poll, interval);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [pauseOnHidden, isPolling, paused, interval, poll]);
  return { isPolling, lastPoll, poll, start, stop };
}
function usePolledFetch(url, options = {}) {
  const { interval = 6e4, immediate = true, paused = false, pauseOnHidden = true, transform, fallback, skip = false } = options;
  const [data, setData] = useState3(null);
  const [loading, setLoading] = useState3(!skip);
  const [error, setError] = useState3(null);
  const fetchData = useCallback3(async () => {
    if (skip) return;
    if (data === null) {
      setLoading(true);
    }
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      const result = transform ? transform(json) : json;
      setData(result);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Fetch failed";
      setError(errorMessage);
      if (fallback !== void 0 && data === null) {
        setData(fallback);
      }
    } finally {
      setLoading(false);
    }
  }, [url, skip, transform, fallback, data]);
  const { isPolling, lastPoll, poll, start, stop } = usePolling(fetchData, {
    interval,
    immediate,
    paused: paused || skip,
    pauseOnHidden
  });
  return {
    data,
    loading,
    error,
    lastPoll,
    isPolling,
    refetch: poll,
    start,
    stop
  };
}

// src/useLocalStorage.ts
import { useState as useState4, useEffect as useEffect4, useCallback as useCallback4, useRef as useRef3 } from "react";
var EMPTY_ARRAY = Object.freeze([]);
function isLocalStorageAvailable() {
  if (typeof window === "undefined") return false;
  try {
    const testKey = "__storage_test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}
function useLocalStorage(key, defaultValue, options = {}) {
  const {
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    syncTabs = true
  } = options;
  const [isAvailable] = useState4(isLocalStorageAvailable);
  const defaultValueRef = useRef3(defaultValue);
  const serializeRef = useRef3(serialize);
  const deserializeRef = useRef3(deserialize);
  defaultValueRef.current = defaultValue;
  serializeRef.current = serialize;
  deserializeRef.current = deserialize;
  const readValue = useCallback4(() => {
    if (!isAvailable) return defaultValue;
    try {
      const item = window.localStorage.getItem(key);
      return item !== null ? deserializeRef.current(item) : defaultValueRef.current;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValueRef.current;
    }
  }, [key, isAvailable]);
  const [storedValue, setStoredValue] = useState4(readValue);
  const storedValueRef = useRef3(storedValue);
  const skipNextDefaultWriteRef = useRef3(false);
  storedValueRef.current = storedValue;
  useEffect4(() => {
    if (!isAvailable) return;
    try {
      if (window.localStorage.getItem(key) === null) {
        if (skipNextDefaultWriteRef.current) {
          skipNextDefaultWriteRef.current = false;
          return;
        }
        window.localStorage.setItem(key, serializeRef.current(storedValue));
      }
    } catch (error) {
      console.warn(`Error initializing localStorage key "${key}":`, error);
    }
  }, [key, isAvailable, storedValue]);
  const setValue = useCallback4(
    (value) => {
      try {
        const newValue = value instanceof Function ? value(storedValueRef.current) : value;
        storedValueRef.current = newValue;
        setStoredValue(newValue);
        if (isAvailable) {
          const serializedValue = serializeRef.current(newValue);
          window.localStorage.setItem(key, serializedValue);
          window.dispatchEvent(new StorageEvent("storage", {
            key,
            newValue: serializedValue,
            storageArea: window.localStorage
          }));
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, isAvailable]
  );
  const remove = useCallback4(() => {
    try {
      const newValue = defaultValueRef.current;
      storedValueRef.current = newValue;
      skipNextDefaultWriteRef.current = true;
      setStoredValue(newValue);
      if (isAvailable) {
        window.localStorage.removeItem(key);
        window.dispatchEvent(new StorageEvent("storage", {
          key,
          newValue: null,
          storageArea: window.localStorage
        }));
      }
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, isAvailable]);
  useEffect4(() => {
    if (!syncTabs || !isAvailable) return;
    const handleStorageChange = (e) => {
      if (e.key !== key || e.storageArea !== window.localStorage) return;
      try {
        const newValue = e.newValue !== null ? deserializeRef.current(e.newValue) : defaultValueRef.current;
        storedValueRef.current = newValue;
        setStoredValue(newValue);
      } catch (error) {
        console.warn(`Error parsing storage event for "${key}":`, error);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key, syncTabs, isAvailable]);
  useEffect4(() => {
    setStoredValue(readValue());
  }, [key, readValue]);
  return {
    value: storedValue,
    setValue,
    remove,
    isAvailable
  };
}
function useLocalStorageBoolean(key, defaultValue = false) {
  const state = useLocalStorage(key, defaultValue, {
    serialize: (v) => String(v),
    deserialize: (v) => v === "true"
  });
  const toggle = useCallback4(() => {
    state.setValue((prev) => !prev);
  }, [state]);
  return { ...state, toggle };
}
function useLocalStorageArray(key, defaultValue = EMPTY_ARRAY) {
  const state = useLocalStorage(key, defaultValue);
  const add = useCallback4((item) => {
    state.setValue((prev) => [...prev, item]);
  }, [state]);
  const removeItem = useCallback4((predicate) => {
    state.setValue((prev) => prev.filter((item) => !predicate(item)));
  }, [state]);
  const clear = useCallback4(() => {
    state.setValue([]);
  }, [state]);
  return {
    ...state,
    items: state.value,
    add,
    removeItem,
    clear
  };
}

// src/usePersistentState.ts
import { useState as useState5, useEffect as useEffect5, useCallback as useCallback5 } from "react";
function usePersistentState(initialValue, options) {
  const { key, ttl, storage = "localStorage" } = options;
  const [state, setState] = useState5(() => {
    if (typeof window === "undefined") return initialValue;
    try {
      const store = window[storage];
      const item = store.getItem(key);
      if (item) {
        const parsed = JSON.parse(item);
        if (ttl && parsed.expiry && Date.now() > parsed.expiry) {
          store.removeItem(key);
          return initialValue;
        }
        return parsed.value ?? parsed;
      }
    } catch (error) {
      console.warn(`[PersistentState] Failed to load key "${key}":`, error);
    }
    return initialValue;
  });
  useEffect5(() => {
    if (typeof window === "undefined") return;
    try {
      const store = window[storage];
      const data = ttl ? { value: state, expiry: Date.now() + ttl } : state;
      store.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.warn(`[PersistentState] Failed to save key "${key}":`, error);
    }
  }, [key, state, storage, ttl]);
  const clear = useCallback5(() => {
    if (typeof window === "undefined") return;
    window[storage].removeItem(key);
    setState(initialValue);
  }, [key, storage, initialValue]);
  return [state, setState, clear];
}

// src/useDebounce.ts
import { useState as useState6, useEffect as useEffect6, useRef as useRef4, useCallback as useCallback6, useMemo } from "react";
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState6(value);
  useEffect6(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
function useDebouncedCallback(fn, options = {}) {
  const {
    delay = 300,
    leading = false,
    trailing = true,
    maxWait
  } = options;
  const fnRef = useRef4(fn);
  const timeoutRef = useRef4(null);
  const maxTimeoutRef = useRef4(null);
  const lastArgsRef = useRef4(null);
  const lastCallTimeRef = useRef4(null);
  const [isPending, setIsPending] = useState6(false);
  useEffect6(() => {
    fnRef.current = fn;
  }, [fn]);
  const cancel = useCallback6(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
    lastArgsRef.current = null;
    lastCallTimeRef.current = null;
    setIsPending(false);
  }, []);
  const flush = useCallback6(() => {
    if (lastArgsRef.current !== null) {
      fnRef.current(...lastArgsRef.current);
      cancel();
    }
  }, [cancel]);
  const callback = useCallback6(
    (...args) => {
      const now = Date.now();
      const isFirstCall = lastCallTimeRef.current === null;
      lastArgsRef.current = args;
      lastCallTimeRef.current = now;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (leading && isFirstCall) {
        fnRef.current(...args);
        lastArgsRef.current = null;
      } else {
        setIsPending(true);
      }
      if (trailing) {
        timeoutRef.current = setTimeout(() => {
          if (lastArgsRef.current !== null) {
            fnRef.current(...lastArgsRef.current);
          }
          cancel();
        }, delay);
      }
      if (maxWait !== void 0 && !maxTimeoutRef.current) {
        maxTimeoutRef.current = setTimeout(() => {
          if (lastArgsRef.current !== null) {
            fnRef.current(...lastArgsRef.current);
          }
          cancel();
        }, maxWait);
      }
    },
    [delay, leading, trailing, maxWait, cancel]
  );
  useEffect6(() => cancel, [cancel]);
  return useMemo(
    () => ({ callback, cancel, flush, isPending }),
    [callback, cancel, flush, isPending]
  );
}
function useDebouncedState(initialValue, delay) {
  const [value, setValue] = useState6(initialValue);
  const debouncedValue = useDebounce(value, delay);
  return [value, setValue, debouncedValue];
}
function useThrottle(value, interval) {
  const [throttledValue, setThrottledValue] = useState6(value);
  const lastUpdated = useRef4(0);
  useEffect6(() => {
    const now = Date.now();
    const elapsed = now - lastUpdated.current;
    if (elapsed >= interval) {
      setThrottledValue(value);
      lastUpdated.current = now;
    } else {
      const handler = setTimeout(() => {
        setThrottledValue(value);
        lastUpdated.current = Date.now();
      }, interval - elapsed);
      return () => clearTimeout(handler);
    }
  }, [value, interval]);
  return throttledValue;
}

// src/useI18n.tsx
import React from "react";
import { jsx } from "react/jsx-runtime";
var STORAGE_KEY = "wt-lang";
var LANG_CHANGED_EVENT = "wt-lang-changed";
var DEFAULT_LANG = "de";
var detectBrowserLang = (supported) => {
  if (typeof navigator === "undefined") return DEFAULT_LANG;
  const prefs = Array.isArray(navigator.languages) ? navigator.languages : [navigator.language];
  for (const entry of prefs) {
    if (!entry) continue;
    const lower = entry.toLowerCase();
    const langOnly = lower.split("-")[0];
    if (supported.includes(lower)) return lower;
    if (supported.includes(langOnly)) return langOnly;
  }
  return DEFAULT_LANG;
};
var getNested = (obj, path) => {
  const result = path.split(".").reduce(
    (acc, part) => acc && typeof acc === "object" && part in acc ? acc[part] : void 0,
    obj
  );
  return typeof result === "string" ? result : void 0;
};
function createI18n(messages) {
  const availableLanguages = Object.keys(messages);
  const defaultContext = {
    lang: DEFAULT_LANG,
    t: (key, fallback) => fallback ?? key,
    setLang: () => {
    },
    availableLanguages
  };
  const I18nContext = React.createContext(defaultContext);
  const I18nProvider = ({ children }) => {
    const [lang, setLangState] = React.useState(DEFAULT_LANG);
    React.useEffect(() => {
      const stored = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      const initial = stored || document.documentElement.lang || detectBrowserLang(availableLanguages);
      setLangState(initial);
      document.documentElement.lang = initial;
    }, []);
    React.useEffect(() => {
      const handler = (ev) => {
        const detailLang = ev.detail?.lang;
        if (detailLang && availableLanguages.includes(detailLang)) {
          setLangState(detailLang);
          document.documentElement.lang = detailLang;
        }
      };
      window.addEventListener(LANG_CHANGED_EVENT, handler);
      return () => window.removeEventListener(LANG_CHANGED_EVENT, handler);
    }, []);
    const setLang = React.useCallback(
      (newLang) => {
        if (!availableLanguages.includes(newLang)) return;
        setLangState(newLang);
        document.documentElement.lang = newLang;
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(STORAGE_KEY, newLang);
        }
        window.dispatchEvent(
          new CustomEvent(LANG_CHANGED_EVENT, { detail: { lang: newLang } })
        );
      },
      []
      // availableLanguages is outer scope, never changes
    );
    const t = React.useCallback(
      (key, fallback) => {
        const msg = getNested(messages[lang] || {}, key);
        return msg ?? fallback ?? key;
      },
      [lang]
    );
    const value = React.useMemo(
      () => ({ lang, t, setLang, availableLanguages }),
      [lang, t, setLang]
    );
    return /* @__PURE__ */ jsx(I18nContext.Provider, { value, children });
  };
  const useI18n = () => React.useContext(I18nContext);
  return { I18nProvider, useI18n, I18nContext };
}
export {
  createI18n,
  useAuth,
  useDebounce,
  useDebouncedCallback,
  useDebouncedState,
  useDisplayName,
  useFetch,
  useLocalStorage,
  useLocalStorageArray,
  useLocalStorageBoolean,
  useMutation,
  usePersistentState,
  usePolledFetch,
  usePolling,
  useThrottle
};
